import { ArrowLeft, ChevronDown, Search } from 'lucide-react';
import { useMemo, useRef, useState, type ReactNode } from 'react';
import { useObserverAnchor } from '@/components/observer/useObserverAnchor';
import { IconButton, Input, Skeleton, Tooltip, cn } from '@/components/ui';
import type { AppInfo, MenuGroup, MenuItem } from './types';
import { useSlidingBox } from './useSlidingBox';

/*
  AppMenu — меню приложения, уровень 3 (docs/foundation.md). Отдельная карточка в цвет страницы:
  сразу видно, что ты внутри приложения. Шапка — «←», значок и имя приложения, состояние, поиск
  по приложению. Пункты текстом, без значков: этим уровень и отличается от второго. Группы
  сворачиваются; внизу общий блок «Управление», его даёт Kaname. Пока приложение открывается, на
  месте пунктов стоят полоски той же высоты (R31) — переход не дёргается; шапка видна сразу. Открытие
  встало на сбое — полоски замирают. Пункты приходят без анимации (R23). Пункт — цель помощника
  `zone.<key>`: капля встаёт в зазоре справа от карточки, против пункта (R36, «следующий шаг»).
*/

interface AppMenuProps {
  app: AppInfo;
  /** Строка под именем: «● Метрики · 12 с». */
  status?: ReactNode;
  groups: MenuGroup[];
  /** Блок «Управление»: настройки, доступ, журнал, состояние — по правам. */
  manage?: MenuItem[];
  activeKey: string | null;
  onSelect: (item: MenuItem) => void;
  onBack: () => void;
  /** Куда ведёт «←»: «Marketplace». */
  backLabel: string;
  searchPlaceholder?: string;
  /** Итог внизу: «71 узел · 12 локаций». */
  footer?: ReactNode;
  /** Пункты ещё едут: на их месте заглушка той же формы. */
  loading?: boolean;
  /** Открытие встало (сбой на шаге): заглушка замирает без блеска. */
  stalled?: boolean;
}

const ACTIVE = '[data-nav-active="true"]';

export function AppMenu({
  app,
  status,
  groups,
  manage,
  activeKey,
  onSelect,
  onBack,
  backLabel,
  searchPlaceholder = 'Найти в приложении',
  footer,
  loading = false,
  stalled = false,
}: AppMenuProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { box, ready } = useSlidingBox(ref, ACTIVE, `${app.name}:${activeKey}:${loading}`);
  const Icon = app.icon;

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-lg border border-line bg-canvas text-fg shadow-inner-card">
      <div className="flex shrink-0 flex-col gap-3 px-3 pt-3 pb-2">
        <div className="flex items-center gap-2">
          <Tooltip content={`Назад: ${backLabel}`}>
            <IconButton aria-label={`Назад: ${backLabel}`} variant="ghost" size="sm" onClick={onBack}>
              <ArrowLeft />
            </IconButton>
          </Tooltip>
          <span className="grid size-8 shrink-0 place-items-center rounded-md border border-line bg-muted text-fg">
            <Icon className="size-4" />
          </span>
          <div className="flex min-w-0 flex-col">
            <h2 className="truncate text-sm font-semibold">{app.name}</h2>
            {status && <span className="truncate text-2xs text-fg-subtle">{status}</span>}
          </div>
        </div>
        <Input placeholder={searchPlaceholder} leadingIcon={<Search />} aria-label={searchPlaceholder} />
      </div>

      <div ref={ref} className="relative min-h-0 flex-1 overflow-y-auto px-2 pb-2">
        <span
          aria-hidden
          data-ready={ready || undefined}
          className="pointer-events-none absolute rounded-sm bg-hover nav-slide"
          style={{
            top: box.top,
            left: box.left,
            width: box.width,
            height: box.height,
            opacity: box.visible ? 1 : 0,
          }}
        />
        {loading ? (
          <MenuSkeleton stalled={stalled} />
        ) : (
          <>
            {groups.map((g, i) => (
              <Group key={g.title ?? i} title={g.title} first={i === 0}>
                {g.items.map((item) => (
                  <Item key={item.key} item={item} active={item.key === activeKey} onSelect={onSelect} />
                ))}
              </Group>
            ))}
            {manage && manage.length > 0 && (
              <Group title="Управление">
                {manage.map((item) => (
                  <Item key={item.key} item={item} active={item.key === activeKey} onSelect={onSelect} />
                ))}
              </Group>
            )}
          </>
        )}
      </div>

      {footer && (
        <div className="shrink-0 border-t border-line px-4 py-3 text-2xs text-fg-subtle tabular-nums">
          {footer}
        </div>
      )}
    </div>
  );
}

/** Группа: заголовок-надстрочник со стрелкой сворачивает её; разделитель сверху. */
function Group({ title, first, children }: { title?: string; first?: boolean; children: ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className={cn('relative flex flex-col gap-0.5', !first && 'mt-3 border-t border-line pt-3')}>
      {title && (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="flex h-7 w-full items-center justify-between rounded-sm px-2 text-2xs font-semibold tracking-wider text-fg-subtle uppercase transition-interactive hover:text-fg"
        >
          {title}
          <ChevronDown
            className={cn('size-3.5 transition-transform duration-normal', !open && '-rotate-90')}
          />
        </button>
      )}
      {open && children}
    </div>
  );
}

function Item({
  item,
  active,
  onSelect,
}: {
  item: MenuItem;
  active: boolean;
  onSelect: (item: MenuItem) => void;
}) {
  const meta = useMemo(() => ({ role: 'source' as const, label: item.label, level: 3 }), [item.label]);
  const anchor = useObserverAnchor<HTMLButtonElement>(`zone.${item.key}`, meta);
  return (
    <button
      ref={anchor}
      type="button"
      onClick={() => onSelect(item)}
      data-nav-active={active || undefined}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'relative flex h-9 w-full items-center justify-between gap-2 rounded-sm px-2 text-sm transition-interactive outline-none',
        active ? 'font-medium text-fg' : 'text-fg-muted hover:bg-hover hover:text-fg',
      )}
    >
      <span className="truncate">{item.label}</span>
      {item.count !== undefined && (
        <span
          className={cn(
            'shrink-0 tabular-nums',
            item.countTone && item.count > 0
              ? cn(
                  'rounded-xs px-1.5 py-0.5 text-2xs leading-none font-semibold text-on-brand',
                  item.countTone === 'danger' ? 'bg-danger' : 'bg-brand',
                )
              : 'text-2xs text-fg-subtle',
          )}
        >
          {item.count}
        </span>
      )}
    </button>
  );
}

/** Заглушка группы: заголовок-надстрочник с разделителем сверху и полоски пунктов. */
const SKELETON_GROUPS = [
  { head: null, items: ['w-20', 'w-28', 'w-24', 'w-40', 'w-20'] },
  { head: 'w-24', items: ['w-32', 'w-28', 'w-40'] },
  // «Управление» — общий блок внизу, его даёт Kaname.
  { head: 'w-20', items: ['w-20', 'w-16', 'w-16', 'w-20'] },
];

/**
 * Заглушка повторяет форму меню: те же высоты пунктов, места заголовков групп и блок «Управление» (R31).
 * stalled — открытие встало (сбой на шаге): блеск замирает, заглушка гаснет наполовину.
 */
function MenuSkeleton({ stalled }: { stalled: boolean }) {
  return (
    <div
      className={cn(stalled && 'opacity-50')}
      aria-label={stalled ? 'Меню не загрузилось' : 'Меню загружается'}
    >
      {SKELETON_GROUPS.map((g, n) => (
        <div key={n} className={cn('flex flex-col gap-0.5', n > 0 && 'mt-3 border-t border-line pt-3')}>
          {g.head && (
            <div className="flex h-7 items-center px-2">
              <Skeleton still={stalled} className={cn('h-2', g.head)} />
            </div>
          )}
          {g.items.map((w, i) => (
            <div key={i} className="flex h-9 items-center px-2">
              <Skeleton still={stalled} className={cn('h-3', w)} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
